import React, { useEffect, useState, useContext } from 'react';
import { Col, Row, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { BsTrash } from 'react-icons/bs'; // Trocando para ícone disponível
import Breadcrumbs from '../../components/layout/breadcrumbs';
import Page from '../../components/page';
import Org from '../../components/single-org';
import { api } from '../../providers/apiClient';
import { ContextUser } from '../../providers/ContextUser';
import { ContextSearch } from '../../providers/ContextSearch';
import OrgSkeleton from '../../components/org-skeleton';
import Search from '../../components/search';
import NoResults from '../../components/no-result';
import { OrgCardsContainer, AdminHeader, SearchSection, FilterButton } from './styles';
import { applyTextFilters } from '../../utils';

const PageAdmin = ({ isCollapsed }) => {
  const navigate = useNavigate();
  const { handleUpdateCurrentOrganization, currentUser, userHierarchy, getRedirectUrl } = useContext(ContextUser);
  const { searchValues, setSearchValues, clearPageFilters } = useContext(ContextSearch);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [isFetching, setIsFetching] = useState(true);
  const [types, setTypes] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [searchOrgResult, setSearchOrgResult] = useState([]);

  const states = [
    { code: '', name: 'Busque por UF...', id: 'state-all' },
    { code: 'AC', name: 'Acre', id: 'state-ac' },
    { code: 'AL', name: 'Alagoas', id: 'state-al' },
    { code: 'AP', name: 'Amapá', id: 'state-ap' },
    { code: 'AM', name: 'Amazonas', id: 'state-am' },
    { code: 'BA', name: 'Bahia', id: 'state-ba' },
    { code: 'CE', name: 'Ceará', id: 'state-ce' },
    { code: 'DF', name: 'Distrito Federal', id: 'state-df' },
    { code: 'ES', name: 'Espírito Santo', id: 'state-es' },
    { code: 'GO', name: 'Goiás', id: 'state-go' },
    { code: 'MA', name: 'Maranhão', id: 'state-ma' },
    { code: 'MT', name: 'Mato Grosso', id: 'state-mt' },
    { code: 'MS', name: 'Mato Grosso do Sul', id: 'state-ms' },
    { code: 'MG', name: 'Minas Gerais', id: 'state-mg' },
    { code: 'PA', name: 'Pará', id: 'state-pa' },
    { code: 'PB', name: 'Paraíba', id: 'state-pb' },
    { code: 'PR', name: 'Paraná', id: 'state-pr' },
    { code: 'PE', name: 'Pernambuco', id: 'state-pe' },
    { code: 'PI', name: 'Piauí', id: 'state-pi' },
    { code: 'RJ', name: 'Rio de Janeiro', id: 'state-rj' },
    { code: 'RN', name: 'Rio Grande do Norte', id: 'state-rn' },
    { code: 'RS', name: 'Rio Grande do Sul', id: 'state-rs' },
    { code: 'RO', name: 'Rondônia', id: 'state-ro' },
    { code: 'RR', name: 'Roraima', id: 'state-rr' },
    { code: 'SC', name: 'Santa Catarina', id: 'state-sc' },
    { code: 'SP', name: 'São Paulo', id: 'state-sp' },
    { code: 'SE', name: 'Sergipe', id: 'state-se' },
    { code: 'TO', name: 'Tocantins', id: 'state-to' },
  ];

  // Redireciona usuários não-ADMIN para seu dashboard contextual ao abrir '/'
  useEffect(() => {
    if (!currentUser) return;
    if (currentUser.role === 'ADMIN' || currentUser.role === 'ACCESS_STAFF') return;

    const h = userHierarchy;
    let target = null;
    switch (currentUser.role) {
      case 'ACCESS_ORG_LEVEL':
        target = h?.organizationId || currentUser.allowedId;
        if (target) navigate(`/prefeitura/${target}`, { replace: true });
        break;
      case 'ACCESS_BRANCH_LEVEL':
        target = h?.branchId || currentUser.allowedId;
        if (target) navigate(`/prefeitura/secretaria/${target}`, { replace: true });
        break;
      case 'ACCESS_UNIT_LEVEL':
        target = h?.unitId || currentUser.allowedId;
        if (target) navigate(`/prefeitura/secretaria/unidade/${target}`, { replace: true });
        break;
      case 'ACCESS_DEPARTMENT_LEVEL':
        target = h?.departmentId || currentUser.allowedId;
        if (target) navigate(`/prefeitura/secretaria/unidade/departamento/${target}`, { replace: true });
        break;
      default:
        // fallback para regra existente
        navigate(getRedirectUrl(currentUser.role, currentUser.allowedId), { replace: true });
    }
  }, [currentUser, userHierarchy]);

  // Carrega dados apenas uma vez na montagem do componente
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsFetching(true);
        
        // Carrega dados e tipos em paralelo
        const [orgsResponse, typesResponse] = await Promise.all([
          api.get(`/users/orgs`),
          api.get('/auth/types')
        ]);

        const allOrgs = orgsResponse.data.orgs;
        const types = typesResponse.data;
        
        // Configura tipos
        const newElement = {
          id: 0,
          description: 'Busque por tipo...',
          level: 0,
        };
        types.unshift(newElement);
        
        // Atualiza estados
        setOrgs(allOrgs);
        setTypes(types);
        setBreadcrumbs(orgsResponse.data.breadcrumbs);
        handleUpdateCurrentOrganization(orgsResponse.data.organization);
        
        // Aplica filtros salvos após carregar dados
        applyFiltersLocally(allOrgs, searchValues['admin'] || {});
        
      } catch (error) {
        console.error('Erro ao carregar dados:', error);
      } finally {
        setIsFetching(false);
      }
    };

    loadData();
  }, []); // Sem dependências - executa apenas uma vez

  // Aplica filtros localmente quando searchValues mudar
  useEffect(() => {
    if (orgs.length > 0) {
      applyFiltersLocally(orgs, searchValues['admin'] || {});
    }
  }, [searchValues, orgs]);



  // Função para aplicar filtros localmente sem chamar API
  const applyFiltersLocally = (allOrgs, filters) => {
    let filteredOrgs = [...allOrgs];
    
    // Filtro por texto usando função centralizada
    if (filters.text && filters.text.trim()) {
      const searchFields = ['name', 'responsible.name', 'responsible.email'];
      applyTextFilters(allOrgs, filters, searchFields, setSearchOrgResult);
      return; // A função applyTextFilters já atualiza o estado
    }
    
    // Filtro por estado
    if (filters.state && filters.state !== '') {
      filteredOrgs = filteredOrgs.filter(org => org.address?.stateCode === filters.state);
    }
    
    // Filtro por tipo
    if (filters.type && filters.type !== '' && filters.type !== 0 && filters.type !== '0') {
      filteredOrgs = filteredOrgs.filter(org => org.type?.id === parseInt(filters.type));
    }
    
    setSearchOrgResult(filteredOrgs);
  };

  const handleNavigate = (item) => {
    navigate(`/prefeitura/${item.id}`);
  };

  const handleClearFilters = () => {
    clearPageFilters('admin');
    // Limpa também o localStorage e salva como filtros vazios
    const currentFilters = JSON.parse(localStorage.getItem('filters') || '{}');
    currentFilters.admin = { state: '', text: '', type: '' };
    localStorage.setItem('filters', JSON.stringify(currentFilters));
  };

  const hasActiveFilters = (searchValues['admin']?.text && searchValues['admin'].text.trim()) || 
                          (searchValues['admin']?.state && searchValues['admin'].state !== '') || 
                          (searchValues['admin']?.type && searchValues['admin'].type !== '' && searchValues['admin'].type !== 0 && searchValues['admin'].type !== '0');

  return (
    <>
      <Breadcrumbs id='breadcrumbs-admin' path={breadcrumbs} />
      <Page id='page-admin' title='Admin'>
        <AdminHeader id='admin-header'>
          <SearchSection id='admin-search-section'>
            <Search
              id='search-admin'
              data={orgs}
              fields={['name', 'responsible.name', 'responsible.email']}
              placeholder='Busque por prefeitura ou dados do responsável...'
              setSearchResult={setSearchOrgResult}
              onSearchTextChange={setSearchValues}
              pageName='admin'
              states={states}
              types={types}
            />
          </SearchSection>
          {hasActiveFilters && (
            <FilterButton id='admin-clear-filters-btn' onClick={handleClearFilters}>
              <BsTrash />
              Limpar Filtros
            </FilterButton>
          )}
        </AdminHeader>

        {isFetching ? (
          <Row id='org-cards-skeleton'>
            {[0, 1, 2].map((item) => (
              <Col key={item} md={isCollapsed ? 3 : 4} className='mb-4'>
                <OrgSkeleton id={`skeleton-${item}`} />
              </Col>
            ))}
          </Row>
        ) : searchOrgResult.length > 0 ? (
          <Row id='org-cards'>
            {searchOrgResult?.map((item, index) => (
              <Col
                id={`org-card-${item.id}`}
                style={{ marginBottom: '20px'}}
                md={isCollapsed ? 3 : 4}
                key={item.id}
                onClick={() => handleNavigate(item)}
              >
                <Org item={item} />
              </Col>
            ))}
          </Row>
        ) : (
          <OrgCardsContainer id='org-cards'>
            <NoResults
              id='no-results-admin'
              message='Nenhuma prefeitura encrontrada'
              searchText={searchValues['admin']?.text || ''}
            />
          </OrgCardsContainer>
        )}
      </Page>
    </>
  );
};

export default PageAdmin;
